import React, { useState, useEffect } from 'react';
import './MaintenancePage.css';
import { assets } from '../../assets/assets';

const MaintenancePage = () => {
  const [launchData, setLaunchData] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const baseURL = window.APP_CONFIG?.API_URL || "https://backendweb.properteck.lk";

  useEffect(() => {
    fetchLaunchStatus();
    
    const statusInterval = setInterval(() => {
      fetchLaunchStatus();
    }, 30000);

    return () => clearInterval(statusInterval);
  }, []);

  useEffect(() => {
    if (launchData?.scheduledTime) {
      calculateCountdown();
      
      const countdownInterval = setInterval(() => {
        calculateCountdown();
      }, 1000);

      return () => clearInterval(countdownInterval);
    } else {
      setTimeRemaining(null);
    }
  }, [launchData?.scheduledTime]);

  const fetchLaunchStatus = async () => {
    try {
      const response = await fetch(`${baseURL}/api/maintenance/status`);
      const data = await response.json();
      if (data && data.success) {
        setLaunchData(data.maintenance);
      }
    } catch (error) {
      console.error('Error fetching launch status:', error);
      // Set default launch data for demo
      setLaunchData({
        scheduledTime: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateCountdown = () => {
    if (!launchData?.scheduledTime) {
      setTimeRemaining(null);
      return;
    }

    const now = new Date().getTime();
    const scheduled = new Date(launchData.scheduledTime).getTime();
    const diffMs = scheduled - now;

    if (diffMs <= 0) {
      setTimeRemaining({ 
        days: 0, 
        hours: 0, 
        minutes: 0, 
        seconds: 0,
        expired: true 
      });
      return;
    }

    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

    setTimeRemaining({ 
      days, 
      hours, 
      minutes, 
      seconds,
      expired: false 
    });
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email && email.includes('@')) {
      setSubscribed(true);
      setTimeout(() => {
        setEmail('');
        setSubscribed(false);
      }, 3000);
    }
  };

  if (loading) {
    return (
      <div className="mainten-container">
        <div className="mainten-loading-screen">
          <div className="mainten-loader"></div>
          <p>Initializing...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mainten-container">
      {/* Animated Background */}
      <div className="mainten-circuit-bg">
        <div className="mainten-circuit-line mainten-line-1"></div>
        <div className="mainten-circuit-line mainten-line-2"></div>
        <div className="mainten-circuit-line mainten-line-3"></div>
        <div className="mainten-circuit-dot mainten-dot-1"></div>
        <div className="mainten-circuit-dot mainten-dot-2"></div>
        <div className="mainten-circuit-dot mainten-dot-3"></div>
        <div className="mainten-circuit-dot mainten-dot-4"></div>
      </div>

      {/* Main Content */}
      <div className="mainten-content">
        {/* Logo/Brand Section */}
        <div className="mainten-brand-section">
          <img src={assets.logo_new} alt="Company Logo" className="mainten-brand-logo" />
        </div>

        {/* Status Badge */}
        <div className="mainten-status-badge">
          <span className="mainten-status-pulse"></span>
          <span className="mainten-status-text">SYSTEM LAUNCHING</span>
        </div>

        {/* Main Title */}
        <div className="mainten-hero-section">
          <h2 className="mainten-hero-title">
            <span className="mainten-title-line">PREPARING FOR</span>
            <span className="mainten-title-line mainten-highlight">LAUNCH</span>
          </h2>
          <p className="mainten-hero-subtitle">
            Your ultimate destination for high-performance computing hardware, 
            gaming rigs, and cutting-edge technology is almost here
          </p>
        </div>

        {/* Countdown Timer */}
        {timeRemaining && !timeRemaining.expired && (
          <div className="mainten-countdown-section">
            <div className="mainten-countdown-label">LAUNCH COUNTDOWN</div>
            <div className="mainten-countdown-grid">
              <div className="mainten-time-block">
                <div className="mainten-time-display">
                  <span className="mainten-time-number">{timeRemaining.days.toString().padStart(2, '0')}</span>
                </div>
                <div className="mainten-time-unit">DAYS</div>
              </div>
              <div className="mainten-time-separator">:</div>
              <div className="mainten-time-block">
                <div className="mainten-time-display">
                  <span className="mainten-time-number">{timeRemaining.hours.toString().padStart(2, '0')}</span>
                </div>
                <div className="mainten-time-unit">HOURS</div>
              </div>
              <div className="mainten-time-separator">:</div>
              <div className="mainten-time-block">
                <div className="mainten-time-display">
                  <span className="mainten-time-number">{timeRemaining.minutes.toString().padStart(2, '0')}</span>
                </div>
                <div className="mainten-time-unit">MINS</div>
              </div>
              <div className="mainten-time-separator">:</div>
              <div className="mainten-time-block">
                <div className="mainten-time-display">
                  <span className="mainten-time-number">{timeRemaining.seconds.toString().padStart(2, '0')}</span>
                </div>
                <div className="mainten-time-unit">SECS</div>
              </div>
            </div>
            
            {launchData?.scheduledTime && (
              <div className="mainten-launch-date">
                {new Date(launchData.scheduledTime).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            )}
          </div>
        )}

        {timeRemaining?.expired && (
          <div className="mainten-launch-ready">
            <div className="mainten-ready-icon">✓</div>
            <h3>WE'RE LIVE!</h3>
            <p>Our store is now online and ready to serve you</p>
            <button onClick={() => window.location.reload()} className="mainten-reload-btn">
              ENTER STORE
            </button>
          </div>
        )}

        {/* Features Grid */}
        <div className="mainten-features-grid">
          <div className="mainten-feature-card">
            <div className="mainten-feature-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z"/>
              </svg>
            </div>
            <h4>High Performance</h4>
            <p>Latest CPUs, GPUs & gaming hardware</p>
          </div>
          
          <div className="mainten-feature-card">
            <div className="mainten-feature-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 6v6l4 2"/>
              </svg>
            </div>
            <h4>Fast Delivery</h4>
            <p>Quick shipping across Sri Lanka</p>
          </div>
          
          <div className="mainten-feature-card">
            <div className="mainten-feature-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
            <h4>Warranty Support</h4>
            <p>Genuine products with full warranty</p>
          </div>
          
          <div className="mainten-feature-card">
            <div className="mainten-feature-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            </div>
            <h4>Expert Support</h4>
            <p>Professional tech consultation</p>
          </div>
        </div>

        {/* Contact Info */}
        <div className="mainten-contact-section">
          <p className="mainten-contact-label">QUESTIONS? REACH OUT TO US</p>
          <div className="mainten-contact-links">
            <a href="mailto:info@properteck.lk" className="mainten-contact-link">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              info@properteck.lk
            </a>
            <a href="tel:+94779490055" className="mainten-contact-link">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
              +94 77 949 0055
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintenancePage;